import { useMemo } from 'react';

import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useSelectedVersion } from '@client/store/ui';
import { OneOfElements } from '@shared/models/project.model';
import { Results } from '@shared/models/unit.model';
import { useVersionResultRecord } from '@features/results/use-results.hooks';
import { useElementSelectedProposalColor } from '../hooks/use-proposals.hooks';
import type { ProposalBar } from './ElementListRow';
import {
  attachBaselineCo2BehindProposalBar,
  buildProposalBarEntry,
  getBaselineCo2ValuePerGfaForElement,
} from './proposal-bar.helpers';

type GetResultsPerGFA = (results: Results | undefined) => Results;

type UseElementListRowProposalBarsParams = {
  element: OneOfElements;
  elementResults: Results;
  getResultsPerGFA: GetResultsPerGFA;
};

/**
 * Proposal bar payloads for {@link ElementListRow}:
 * Returns a single bar colored by {@link useElementSelectedProposalColor}
 * (baseline grey unless the selected proposal affects the element).
 *
 * When a proposal is selected and the element is affected by it (i.e. the
 * bar color is not baseline grey), `baselineCo2Value` is populated from
 * lifecycle-filtered {@link useVersionResultRecord} for the selected version
 * so the row renders a grey baseline bar behind the proposal bar.
 */
export const useElementListRowProposalBars = ({
  element,
  elementResults,
  getResultsPerGFA,
}: UseElementListRowProposalBarsParams): ProposalBar[] => {
  const displayCurrency = useDisplayCurrency();
  const elementColor = useElementSelectedProposalColor(element);
  const selectedProposalId = useSelectedProposalId();
  const selectedVersion = useSelectedVersion();
  const baselineResultRecord = useVersionResultRecord(selectedVersion);

  return useMemo(() => {
    const bar = buildProposalBarEntry(
      getResultsPerGFA(elementResults),
      elementColor,
      displayCurrency,
    );
    return [
      attachBaselineCo2BehindProposalBar(bar, {
        selectedProposalId,
        proposalColor: elementColor,
        hasSelectedVersion: selectedVersion != null,
        baselineCo2Value: getBaselineCo2ValuePerGfaForElement(
          element.id,
          baselineResultRecord,
          getResultsPerGFA,
        ),
      }),
    ];
  }, [
    getResultsPerGFA,
    elementResults,
    elementColor,
    displayCurrency,
    selectedProposalId,
    selectedVersion,
    baselineResultRecord,
    element.id,
  ]);
};
